import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type QuoteRequestRow,
  type ReachablePharmacy,
  type SpeciesKey,
  type WantedItem,
} from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useSector } from '@/lib/sector';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * "Can you fill this?"
 *
 * ── WHY THIS SCREEN EXISTS AT ALL ───────────────────────────────────────────
 *
 * A buyer holding a doctor's prescription cannot shop. Half of what is on the
 * paper is prescription-only and therefore not browsable, the strengths are
 * written in a hand they cannot read, and they have no idea which pharmacy
 * stocks all six items. Searching is the wrong tool; showing somebody the paper
 * is the right one.
 *
 * ── WHY BOTH A PHOTO AND A TYPED LIST ───────────────────────────────────────
 *
 * The photograph is what makes a prescription-only medicine dispensable — a
 * pharmacist has to see the paper. The typed list is what makes the request
 * answerable in a hurry: a pharmacist working between customers reads "Amoxil
 * 500, 21 caps" in a second, where squinting at handwriting costs a minute.
 *
 * Neither is required on its own. What is required is that the request says
 * something, because nobody can price an empty one.
 *
 * ── AND WHAT HAPPENS TO THE PHOTO ───────────────────────────────────────────
 *
 * It does not go out with the request. The request fans out to a handful of
 * nearby pharmacies; the image is opened deliberately, one at a time, and every
 * open is recorded and shown back to the buyer on this screen.
 */
export function PrescriptionScreen() {
  const sector = useSector();
  const isVet = sector === 'veterinary';

  const { data, isLoading } = useQuery({
    queryKey: ['quote-requests'],
    queryFn: ({ signal }) => api.quoteRequests(signal),
  });

  const [composing, setComposing] = useState(false);

  if (composing) {
    return <Compose onDone={() => setComposing(false)} />;
  }

  const requests = data ?? [];

  return (
    <>
      <AppBar back title={isVet ? 'Ask a vet supplier' : 'Ask a pharmacy'} />

      <div className="mx-auto w-full max-w-[720px] px-4 pb-8 pt-4 xl:px-6">
        <RequestHero isVet={isVet} onStart={() => setComposing(true)} />

        {isLoading ? (
          <div className="mt-3 space-y-2.5">
            <Skeleton className="h-24 w-full" radius="card" />
            <Skeleton className="h-24 w-full" radius="card" />
          </div>
        ) : requests.length === 0 ? (
          /* One quiet line, not a second panel.
             This used to be a full EmptyState — a bordered box with its own
             heading, its own sentence and a SECOND "Start a new request"
             button, directly under the first one. Two identical buttons on one
             short screen reads as a mistake, and the empty state was explaining
             a screen that has nothing on it yet, which explains itself. */
          <p className="mt-5 flex items-center justify-center gap-2 text-[12.5px] text-faint">
            <Icon name="clock" size={14} strokeWidth={1.9} />
            Replies will show up here
          </p>
        ) : (
          <div className="mt-5 space-y-2.5">
            <h2 className="m-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
              Your requests
            </h2>
            {requests.map((request) => (
              <RequestCard key={request.reference} request={request} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The panel at the top of the prescription screen.
 *
 * ── WHAT IT REPLACED ──────────────────────────────────────────
 *
 * A bare headline, a three-line paragraph of grey prose, and below it an empty
 * state with a second heading, a second sentence and a duplicate button — all
 * on white. Four blocks of text to say one thing.
 *
 * ── AND WHY IT IS BUILT THIS WAY ─────────────────────────────────
 *
 * The three steps carry the explanation now. A buyer who has never done this
 * does not need a paragraph telling them pharmacies will reply — they need to
 * know how many steps there are and that money comes last. Three words each,
 * read in about a second, and the one thing people actually worry about ("do I
 * pay now?") is answered by step three rather than buried in clause four of a
 * sentence.
 *
 * The numerals are mono, like every other number in this app.
 *
 * The gradient is the same one the trust banner and the prescription card wear.
 * This screen is a different WAY IN rather than a shelf item, and the colour is
 * what says so — the same argument that put the gradient on the card the buyer
 * tapped to get here, so arriving feels like the same object opening.
 */
function RequestHero({ isVet, onStart }: { isVet: boolean; onStart: () => void }) {
  const steps = [
    ['Photograph', 'the paper'],
    // Not "pharmacy prices": at 320px that wrapped to a second line while the
    // other two stayed on one, leaving the row ragged. The line above already
    // says whose prices these are.
    ['Compare', 'the prices'],
    // NOT "at your door". See the note above the send-button line below.
    ['Pay', 'only if you accept'],
  ];

  return (
    <section
      data-testid="prescription-hero"
      className="relative overflow-hidden rounded-[16px] bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-5 py-6 text-white"
    >
      {/* Light falling from the top right. A flat gradient reads as a coloured
          box; this gives it a surface without costing an image request. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_88%_-10%,rgba(255,255,255,0.18),transparent_58%)]"
      />

      <div className="relative">
        <h1 className="m-0 font-display text-[23px] font-bold leading-[1.15] tracking-[-0.025em]">
          Send your prescription
        </h1>

        {/* One line. It was four. */}
        <p className="m-0 mt-1.5 text-[13px] leading-snug text-white/75">
          {isVet ? 'Animal-health sellers' : 'Pharmacies'} near you reply with a price.
        </p>

        {/* Capped, so the three steps stay a group. Left to fill a 1280px
            panel they spread so far apart they stopped reading as one
            sequence and became three unrelated labels. */}
        <ol className="m-0 mt-4 grid max-w-[420px] list-none grid-cols-3 gap-2 p-0">
          {steps.map(([head, tail], index) => (
            <li key={head} className="min-w-0">
              <span className="flex items-center gap-1.5">
                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-white/20 font-mono text-[10px] font-bold leading-none text-white">
                  {index + 1}
                </span>
              </span>
              <span className="mt-1.5 block text-[12.5px] font-bold leading-tight text-white">
                {head}
              </span>
              <span className="block text-[11px] leading-tight text-white/65">{tail}</span>
            </li>
          ))}
        </ol>

        {/* White on the gradient, not the blue primary — a blue button on a blue
            panel is a button nobody sees. Button has no inverted variant and
            one screen does not justify adding one to the design system. */}
        <button
          type="button"
          onClick={onStart}
          data-testid="start-request"
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-btn bg-white px-5 text-[13.5px] font-bold text-blue-deep sm:w-auto"
        >
          Start a new request
        </button>
      </div>
    </section>
  );
}

/** One of the two ways a request can be sent. */
function ReachMode({
  selected,
  onSelect,
  title,
  blurb,
  recommended = false,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  blurb: string;
  recommended?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-card border p-3 text-left',
        selected ? 'border-blue bg-blue-tint-2' : 'border-line bg-white hover:border-faint',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-2',
          selected ? 'border-[5.5px] border-blue bg-white' : 'border-line bg-white',
        )}
      />
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13.5px] font-bold text-ink">{title}</span>
          {recommended ? (
            <span className="rounded-pill bg-blue-tint px-1.5 py-[1px] font-mono text-[9px] uppercase tracking-[0.1em] text-blue">
              Best price
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-muted">{blurb}</span>
      </span>
    </button>
  );
}

function PharmacyChoice({
  pharmacy,
  picking,
  selected,
  onToggle,
}: {
  pharmacy: ReachablePharmacy;
  picking: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  const where = [
    pharmacy.same_lga ? 'In your area' : pharmacy.lga,
    pharmacy.distance_km !== null ? `${pharmacy.distance_km} km` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const inner = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-blue-tint font-display text-[11px] font-bold text-blue">
        {pharmacy.initials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium text-ink">{pharmacy.name}</span>
        <span className="block text-[11.5px] text-faint">
          {where}
          {pharmacy.quality_score > 0 ? ` · ★ ${pharmacy.quality_score.toFixed(1)}` : ''}
        </span>
      </span>
    </>
  );

  if (!picking) {
    return <li className="flex items-center gap-2.5 px-0.5 py-1">{inner}</li>;
  }

  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-btn border p-2 text-left',
          selected ? 'border-blue bg-blue-tint-2' : 'border-line bg-white',
        )}
      >
        {inner}
        <span
          aria-hidden
          className={cn(
            'grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border-2',
            selected ? 'border-blue bg-blue' : 'border-line bg-white',
          )}
        >
          {selected ? (
            <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
              <path
                d="M2.5 6.2l2.4 2.4 4.6-5"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </span>
      </button>
    </li>
  );
}

function RequestCard({ request }: { request: QuoteRequestRow }) {
  const opened = request.sent_to.filter((one) => one.opened_prescription_at).length;

  return (
    <Card flush>
      <Link to={`/prescriptions/${request.reference}`} className="block p-4 no-underline">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            {request.reference}
          </span>
          {request.is_veterinary ? (
            <span className="rounded-pill border border-blue px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.12em] text-blue">
              Animal
            </span>
          ) : null}
          {request.quote_count > 0 ? (
            <Badge tone="brand">
              {request.quote_count} {request.quote_count === 1 ? 'reply' : 'replies'}
            </Badge>
          ) : null}
        </div>

        <p className="m-0 mt-1.5 text-[14px] font-bold text-ink">{request.status_label}</p>

        <p className="m-0 mt-0.5 text-[12.5px] leading-snug text-muted">
          {request.wanted.map((item) => item.name).join(', ') || request.note || 'Prescription photo'}
        </p>

        {/* §9.15 made visible. A buyer is entitled to know where their
            prescription has been, without having to ask. */}
        {request.has_prescription ? (
          <p className="m-0 mt-2 text-[11.5px] text-faint">
            {opened === 0
              ? 'Nobody has opened your prescription yet'
              : `${opened} ${opened === 1 ? 'pharmacy has' : 'pharmacies have'} opened your prescription`}
          </p>
        ) : null}
      </Link>
    </Card>
  );
}

/**
 * Writing one.
 *
 * The photograph goes up first and on its own, so a failed upload on a bad
 * connection costs the photo and not the whole form.
 */
function Compose({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sector = useSector();
  const isVet = sector === 'veterinary';
  const fileInput = useRef<HTMLInputElement>(null);

  const [prescriptionId, setPrescriptionId] = useState<number | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [wanted, setWanted] = useState<WantedItem[]>([{ name: '', quantity: '' }]);
  const [note, setNote] = useState('');
  const [species, setSpecies] = useState<SpeciesKey | ''>('');
  const [animalNote, setAnimalNote] = useState('');

  // Empty means "whoever is nearest", which is the default and what most
  // people want. A non-empty list is somebody exercising a real preference.
  const [chosen, setChosen] = useState<number[]>([]);
  const [picking, setPicking] = useState(false);

  // Null means "wherever I am delivering". Set only when somebody deliberately
  // goes looking elsewhere — in Lagos for the week, or sending to family.
  const [lookingIn, setLookingIn] = useState<string | null>(null);

  const reachable = useQuery({
    queryKey: ['reachable', sector, lookingIn],
    queryFn: ({ signal }) =>
      api.reachablePharmacies({ sector, state: lookingIn ?? undefined }, signal),
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadPrescription(file),
    onSuccess: (result) => setPrescriptionId(result.id),
  });

  const send = useMutation({
    mutationFn: () =>
      api.createQuoteRequest({
        sector,
        prescription_id: prescriptionId ?? undefined,
        wanted: wanted.filter((item) => item.name.trim() !== ''),
        note: note.trim() || undefined,
        species: isVet && species ? species : undefined,
        animal_note: isVet && animalNote.trim() ? animalNote.trim() : undefined,
        pharmacy_ids: chosen.length > 0 ? chosen : undefined,
      }),
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: ['quote-requests'] });
      navigate(`/prescriptions/${request.reference}`);
    },
  });

  const error = send.error instanceof ApiError ? send.error : null;

  const hasSomething =
    prescriptionId !== null ||
    wanted.some((item) => item.name.trim() !== '') ||
    note.trim() !== '';

  return (
    <>
      <AppBar back title="New request" />

      <div className="mx-auto w-full max-w-[560px] px-4 pb-8 pt-4 xl:px-6">
        <button
          type="button"
          onClick={onDone}
          className="-mx-1 mb-3 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
        >
          <Icon name="back" size={14} strokeWidth={2.4} />
          Back
        </button>

        <Card className="!p-5">
          <h2 className="m-0 font-display text-[16px] font-bold tracking-[-0.015em]">
            The prescription
          </h2>

          {/* ────────────────── NO `capture`, DELIBERATELY ──────────────────
              `capture="environment"` used to be here, and it does not mean
              "offer the camera". It means "open the rear camera and nothing
              else": the phone skips its own chooser entirely, so Photo Library,
              Choose File and Google Drive are all unreachable.

              That is wrong for this screen more than most. A prescription is
              usually ALREADY a photograph — taken in the clinic, sent over
              WhatsApp by a relative, scanned to a PDF by the hospital — and
              forcing a buyer to re-photograph a picture they are holding, often
              off another screen, is how a legible document becomes an
              unreadable one.

              Without the attribute the phone shows its full sheet: Photo
              Library, Take Photo, Choose File, and whatever providers are
              installed. Taking a photo is still one tap in; it is just no
              longer the only way. */}
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];

              if (file) {
                setPreview(URL.createObjectURL(file));
                upload.mutate(file);
              }
            }}
          />

          {/* ── A TARGET, NOT A GREY BUTTON ───────────────────────────
              This was a heading, a two-line paragraph and a small grey "Upload
              a photo" button that looked like the least important thing on the
              screen — on a screen whose entire purpose is getting that one
              photograph. Now the photograph is the biggest thing on it. */}
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            data-testid="upload-prescription"
            className={cn(
              'mt-3 flex w-full flex-col items-center justify-center rounded-card border-[1.5px] border-dashed px-4 py-7 text-center',
              prescriptionId
                ? 'border-verified/40 bg-verified-tint'
                : 'border-blue/35 bg-blue-tint-2 hover:border-blue/60',
            )}
          >
            <span
              className={cn(
                'grid h-11 w-11 place-items-center rounded-full',
                prescriptionId ? 'bg-verified/15' : 'bg-blue/10',
              )}
            >
              <Icon
                name={prescriptionId ? 'check' : 'camera'}
                size={21}
                strokeWidth={prescriptionId ? 2.6 : 1.9}
                className={prescriptionId ? 'text-verified' : 'text-blue'}
              />
            </span>

            <span className="mt-2.5 block text-[13.5px] font-bold text-ink">
              {upload.isPending
                ? 'Uploading…'
                : prescriptionId
                  ? 'Uploaded — tap to replace'
                  : 'Upload a photo'}
            </span>

            {/* The chooser offers all of these. Saying so stops somebody
                hunting for a camera they do not need — the paper is usually
                already a picture in their gallery or a PDF from the hospital. */}
            <span className="mt-0.5 block text-[11.5px] text-muted">
              Photo, file or PDF
            </span>
          </button>

          {preview && prescriptionId ? (
            <img
              src={preview}
              alt="The prescription you uploaded"
              className="mt-2.5 max-h-40 w-full rounded-card border border-line object-contain"
            />
          ) : null}

          {/* The promise, once, small, under the thing it is about. */}
          <p className="m-0 mt-2.5 text-[11.5px] leading-snug text-faint">
            Only the {isVet ? 'sellers' : 'pharmacies'} you choose can open it, and you will see
            every one that does.
          </p>
        </Card>

        <Card className="mt-3 !p-5">
          <h2 className="m-0 font-display text-[16px] font-bold tracking-[-0.015em]">
            What do you need?
          </h2>
          {/* The paragraph here explained that a rough name is better than
              nothing. The placeholders show that in two words each, and a
              placeholder is read where a paragraph above it is not. */}
          <p className="m-0 mt-1 text-[12px] text-faint">Whatever you can read off the paper.</p>

          <div className="mt-3 space-y-2.5">
            {wanted.map((item, index) => (
              <div key={index} className="flex gap-2">
                <input
                  value={item.name}
                  onChange={(event) =>
                    setWanted((current) =>
                      current.map((one, i) => (i === index ? { ...one, name: event.target.value } : one)),
                    )
                  }
                  placeholder={isVet ? 'Albendazole bolus' : 'Amoxil 500mg'}
                  className="min-h-11 min-w-0 flex-[2] rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
                />
                <input
                  value={item.quantity ?? ''}
                  onChange={(event) =>
                    setWanted((current) =>
                      current.map((one, i) =>
                        i === index ? { ...one, quantity: event.target.value } : one,
                      ),
                    )
                  }
                  placeholder="21 caps"
                  className="min-h-11 min-w-0 flex-1 rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
                />
              </div>
            ))}
          </div>

          {wanted.length < 20 ? (
            <button
              type="button"
              onClick={() => setWanted((current) => [...current, { name: '', quantity: '' }])}
              className="mt-2.5 min-h-11 bg-transparent p-0 text-[13px] font-bold text-blue"
            >
              Add another
            </button>
          ) : null}

          <label className="mt-3 block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
              Anything else they should know
            </span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              placeholder="Doctor said three times daily for a week."
              className="w-full rounded-btn border border-line bg-white p-2.5 text-[14px] text-ink outline-none focus:border-blue"
            />
          </label>
        </Card>

        {/* Veterinary: a dose is per species and per kilogram. Without these a
            seller cannot quote at all, so they are asked here rather than
            discovered in a back-and-forth that costs the buyer a day. */}
        {isVet ? (
          <Card className="mt-3 border-blue !p-5">
            <h2 className="m-0 font-display text-[16px] font-bold tracking-[-0.015em]">
              About the animal
            </h2>
            <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-muted">
              Dosing is by species and by weight, so nobody can price this without knowing what it
              is for.
            </p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {(
                [
                  ['poultry', 'Poultry'],
                  ['cattle', 'Cattle'],
                  ['goat', 'Goats'],
                  ['sheep', 'Sheep'],
                  ['dog', 'Dogs'],
                  ['cat', 'Cats'],
                  ['pig', 'Pigs'],
                  ['fish', 'Fish'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={species === key}
                  onClick={() => setSpecies(species === key ? '' : key)}
                  className={cn(
                    'min-h-11 rounded-pill border px-3 text-[12.5px] font-medium',
                    species === key
                      ? 'border-blue bg-blue text-white'
                      : 'border-line bg-white text-muted hover:text-ink',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <label className="mt-3 block">
              <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
                How many, and roughly what weight
              </span>
              <input
                value={animalNote}
                onChange={(event) => setAnimalNote(event.target.value)}
                placeholder="Three goats, about 25kg each"
                className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
              />
            </label>
          </Card>
        ) : null}

        {/* ── Who sees this ──────────────────────────────────────────────
            Before the send button, never after. "Pharmacies near you" is a
            promise about somebody's medical paperwork, and a buyer is entitled
            to know which pharmacies — and to choose different ones — before
            they hand it over, not once it has gone. */}
        <Card className="mt-3 !p-5">
          <h2 className="m-0 font-display text-[16px] font-bold tracking-[-0.015em]">
            Who sees this
          </h2>

          <div className="mt-3 space-y-2.5">
            <ReachMode
              selected={chosen.length === 0}
              onSelect={() => {
                setChosen([]);
                setPicking(false);
              }}
              title={`The ${reachable.data?.maxRecipients ?? 5} nearest to you`}
              blurb="See who has all of it, and what it should cost."
              recommended
            />
            <ReachMode
              selected={chosen.length > 0 || picking}
              onSelect={() => setPicking(true)}
              title="A pharmacy I choose"
              blurb="One you trust, or one where you actually are."
            />
          </div>

          {/* ── Somewhere else ───────────────────────────────────────────
              Only once they are picking, because it is the answer to a
              question most people never ask. But for the person travelling —
              who is disproportionately the person who needs a pharmacy in a
              hurry — it is the difference between the app helping and the app
              saying no. */}
          {picking && (reachable.data?.states.length ?? 0) > 1 ? (
            <div className="mt-3 border-t border-line pt-3">
              <p className="m-0 mb-1.5 text-[12.5px] font-medium text-ink">
                Where are you looking?
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(reachable.data?.states ?? []).map((one) => {
                  const active = (lookingIn ?? reachable.data?.state) === one.state;

                  return (
                    <button
                      key={one.state}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        setLookingIn(one.state);
                        // The names you picked in Lagos mean nothing in Abuja.
                        setChosen([]);
                      }}
                      className={cn(
                        'min-h-11 rounded-pill border px-3 text-[12.5px] font-medium',
                        active
                          ? 'border-blue bg-blue text-white'
                          : 'border-line bg-white text-muted hover:text-ink',
                      )}
                    >
                      {one.state}
                      {one.is_yours ? ' · yours' : ''}
                      <span className="ml-1 opacity-70">{one.pharmacies}</span>
                    </button>
                  );
                })}
              </div>

              {lookingIn && lookingIn !== reachable.data?.state ? (
                <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-caution-ink">
                  These are in {lookingIn}, not where your address is. Make sure they can get it to
                  you — or change your delivery address at checkout.
                </p>
              ) : null}
            </div>
          ) : null}

          {/* The list is shown for BOTH modes — in the default it is telling
              you who is about to receive it, which you are owed either way. */}
          {reachable.isLoading ? (
            <Skeleton className="mt-3 h-20 w-full" radius="card" />
          ) : (reachable.data?.pharmacies.length ?? 0) === 0 ? (
            <p className="m-0 mt-3 text-[12.5px] leading-relaxed text-muted">
              No {isVet ? 'animal-health seller' : 'pharmacy'} near you is taking requests yet.
            </p>
          ) : (
            <ul className="m-0 mt-3 list-none space-y-1.5 p-0">
              {(reachable.data?.pharmacies ?? [])
                .filter((one) => picking || chosen.length > 0 || one.would_be_asked)
                .map((one) => (
                  <PharmacyChoice
                    key={one.id}
                    pharmacy={one}
                    picking={picking || chosen.length > 0}
                    selected={chosen.includes(one.id)}
                    onToggle={() =>
                      setChosen((current) =>
                        current.includes(one.id)
                          ? current.filter((id) => id !== one.id)
                          : [...current, one.id],
                      )
                    }
                  />
                ))}
            </ul>
          )}

          {reachable.data?.deliveringTo ? (
            <p className="m-0 mt-3 text-[11.5px] leading-relaxed text-faint">
              Ranked by how close they are to {reachable.data.deliveringTo}.
            </p>
          ) : null}
        </Card>

        {error ? (
          <p role="alert" className="m-0 mt-3 text-[13px] font-medium leading-relaxed text-danger">
            {error.message}
          </p>
        ) : null}

        <Button
          size="lg"
          className="mt-4 w-full"
          disabled={!hasSomething || send.isPending}
          onClick={() => send.mutate()}
        >
          {send.isPending
            ? 'Sending…'
            : chosen.length > 0
              ? `Send to ${chosen.length} ${chosen.length === 1 ? 'pharmacy' : 'pharmacies'}`
              : 'Send to nearby ' + (isVet ? 'sellers' : 'pharmacies')}
        </Button>

        {/* ── WHY THIS NO LONGER SAYS "AT YOUR DOOR" ─────────────────────
            Because it is not true, and it is least true here of anywhere.

            Two transactions leave at checkout: the delivery fee is CHARGED,
            and the goods are AUTHORIZED. When the goods are actually charged
            is CapturePolicy's decision, and it has three answers — checkout,
            dispatch, or door. Dispatch is the default, not door.

            And §9.18 hard-codes POM to CHECKOUT capture, before the tier table
            is even consulted. This is the prescription screen. A prescription
            is POM by definition, so "pay at your door" was wrong by rule on
            the one screen where it mattered most. A first-time buyer is
            charged at checkout too, which is everybody reading this.

            "At your door" also reads as CASH to a Nigerian buyer. There is no
            cash anywhere in ChiCare; every naira moves by card.

            What IS true at this moment, and is the thing somebody hesitates
            over with a thumb on the button, is that asking costs nothing. */}
        <p className="m-0 mt-2 text-center text-[12px] text-faint">
          Free to send. You only pay if you accept a reply.
        </p>
      </div>
    </>
  );
}
